// =============================================================================
// components/nav/dmBadge.ts — tolerant bridge to the DM unread store.
//
// The FloatingTabBar shows a LIVE unread badge on the Chat tab, driven by the
// shared `services/dmState` store (getDmUnreadTotal / onDmUnreadChange). In the
// bare-RN migration that store lives under `services/` which a SEPARATE P2 agent
// is porting concurrently — so this nav component must NOT hard-import it (a
// missing module would break tsc + the build before services lands).
//
// This shim resolves `services/dmState` at runtime via an optional require. When
// the ported store is present, the badge is fully live (subscribe + total). Until
// then it no-ops to 0 (no badge), so the tab bar renders standalone. Once the
// services agent lands `services/dmState.ts` with the same surface
// (getDmUnreadTotal(): number, onDmUnreadChange(cb): () => void) the badge lights
// up with zero further changes here.
// =============================================================================

type UnsubscribeFn = () => void;

interface DmState {
  getDmUnreadTotal: () => number;
  onDmUnreadChange: (cb: () => void) => UnsubscribeFn;
}

let cached: DmState | null | undefined;

function loadDmState(): DmState | null {
  if (cached !== undefined) return cached;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    const mod = require('../../services/dmState');
    if (mod && typeof mod.getDmUnreadTotal === 'function' && typeof mod.onDmUnreadChange === 'function') {
      cached = mod as DmState;
    } else {
      cached = null;
    }
  } catch {
    cached = null;
  }
  return cached;
}

/** Current total DM unread across all conversations. 0 when the store is absent. */
export function getDmUnreadTotal(): number {
  const s = loadDmState();
  if (!s) return 0;
  try {
    return s.getDmUnreadTotal() || 0;
  } catch {
    return 0;
  }
}

/** Subscribe to unread-total changes. Returns an unsubscribe fn (no-op if absent). */
export function onDmUnreadChange(cb: () => void): UnsubscribeFn {
  const s = loadDmState();
  if (!s) return () => {};
  try {
    return s.onDmUnreadChange(cb);
  } catch {
    return () => {};
  }
}
